import React, { useEffect, useMemo, useState } from 'react'
import SidebarNew from '../../components/sidebar/SidebarNew'
import { SearchOutlined } from '@ant-design/icons'
import Swal from 'sweetalert2'
import { getSettings, updateSettings } from '../../api/AdminRequest'
import shippingCountries from '../../utlis/shippingCountries'

const HOME_COUNTRY = 'United Arab Emirates'

function SettingsPage() {
    const [shippingMode, setShippingMode] = useState('worldwide')
    const [enabledCountries, setEnabledCountries] = useState([])
    const [initial, setInitial] = useState({ shippingMode: 'worldwide', enabledCountries: [] })
    const [countrySearch, setCountrySearch] = useState('')
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const { data } = await getSettings()
                if (data.success) {
                    setShippingMode(data.settings.shipping_mode)
                    setEnabledCountries(data.settings.enabled_countries)
                    setInitial({
                        shippingMode: data.settings.shipping_mode,
                        enabledCountries: data.settings.enabled_countries,
                    })
                }
            } catch (error) {
                console.log(error?.response?.data)
                Swal.fire('Error', 'Failed to load settings', 'error')
            } finally {
                setLoading(false)
            }
        }
        fetchSettings()
    }, [])

    const dirty =
        shippingMode !== initial.shippingMode ||
        JSON.stringify([...enabledCountries].sort()) !== JSON.stringify([...initial.enabledCountries].sort())

    const filteredCountries = useMemo(() => {
        const q = countrySearch.trim().toLowerCase()
        if (!q) return shippingCountries
        return shippingCountries.filter((c) => c.toLowerCase().includes(q))
    }, [countrySearch])

    const toggleCountry = (country) => {
        setEnabledCountries((prev) =>
            prev.includes(country) ? prev.filter((c) => c !== country) : [...prev, country]
        )
    }

    const handleSave = async () => {
        if (shippingMode === 'selected' && enabledCountries.length === 0) {
            Swal.fire('Warning', 'Select at least one country, or switch back to Worldwide', 'warning')
            return
        }
        setSaving(true)
        try {
            const { data } = await updateSettings({
                shipping_mode: shippingMode,
                enabled_countries: enabledCountries,
            })
            if (data.success) {
                setInitial({ shippingMode, enabledCountries })
                Swal.fire({
                    icon: 'success',
                    title: 'Settings saved',
                    text:
                        shippingMode === 'worldwide'
                            ? `Shipping is open worldwide (home region: ${HOME_COUNTRY})`
                            : `Shipping enabled for ${enabledCountries.length} countr${enabledCountries.length === 1 ? 'y' : 'ies'} + ${HOME_COUNTRY}`,
                    timer: 2500,
                    showConfirmButton: false,
                })
            }
        } catch (error) {
            console.log(error?.response?.data)
            Swal.fire('Error', error?.response?.data?.message || 'Failed to save settings', 'error')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="flex flex-col">
            <div className="flex flex-col">
                <div className="flex">
                    <div className="h-full z-10 overflow-y-auto">
                        <SidebarNew />
                    </div>
                    <div className="lg:flex-1 overflow-auto scrollbar-hide h-screen w-full md:z-10 p-4 lg:p-10 md:p-10">
                        <div className="flex items-center justify-between gap-3 mb-6">
                            <h1 className="text-2xl font-bold">Settings</h1>
                            <button
                                onClick={handleSave}
                                disabled={loading || saving || !dirty}
                                className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>

                        <div className="max-w-3xl bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
                            <h2 className="text-lg font-semibold">Shipping Availability</h2>
                            <p className="text-sm text-gray-500 mt-1 max-w-xl">
                                Controls which countries can check out. Currency pricing is unaffected - visitors
                                always see converted prices in their local currency.
                            </p>

                            <div className="mt-5 space-y-3">
                                <label
                                    className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                                        shippingMode === 'worldwide' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="shippingMode"
                                        checked={shippingMode === 'worldwide'}
                                        onChange={() => setShippingMode('worldwide')}
                                        className="mt-1 form-radio h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span>
                                        <span className="block font-medium">Worldwide</span>
                                        <span className="block text-sm text-gray-500">
                                            Every country can order. Home region ({HOME_COUNTRY}) included by default.
                                        </span>
                                    </span>
                                </label>

                                <label
                                    className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                                        shippingMode === 'selected' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="shippingMode"
                                        checked={shippingMode === 'selected'}
                                        onChange={() => setShippingMode('selected')}
                                        className="mt-1 form-radio h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span>
                                        <span className="block font-medium">Selected countries only</span>
                                        <span className="block text-sm text-gray-500">
                                            Only the countries you pick below can order, plus {HOME_COUNTRY} (always allowed).
                                        </span>
                                    </span>
                                </label>
                            </div>
                        </div>

                        {shippingMode === 'selected' && (
                            <div className="max-w-3xl bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                                    <h2 className="text-lg font-semibold">
                                        Enabled Countries{' '}
                                        <span className="text-sm font-normal text-gray-400">
                                            ({enabledCountries.length} selected)
                                        </span>
                                    </h2>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setEnabledCountries(shippingCountries)}
                                            className="px-3 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-50"
                                        >
                                            Select all
                                        </button>
                                        <button
                                            onClick={() => setEnabledCountries([])}
                                            className="px-3 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-50"
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </div>

                                <div className="relative mb-3">
                                    <input
                                        type="text"
                                        value={countrySearch}
                                        onChange={(e) => setCountrySearch(e.target.value)}
                                        placeholder="Search country..."
                                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <SearchOutlined className="absolute left-3 top-2.5 text-gray-400" />
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-[420px] overflow-y-auto border border-gray-100 rounded-lg p-3">
                                    {filteredCountries.map((country) => (
                                        <label
                                            key={country}
                                            className={`flex items-center gap-2 px-2.5 py-2 rounded-md text-sm cursor-pointer select-none ${
                                                country === HOME_COUNTRY ? 'opacity-60' : 'hover:bg-gray-50'
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={country === HOME_COUNTRY || enabledCountries.includes(country)}
                                                disabled={country === HOME_COUNTRY}
                                                onChange={() => toggleCountry(country)}
                                                className="form-checkbox h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            {country}
                                            {country === HOME_COUNTRY && (
                                                <span className="ml-auto text-[10px] font-semibold text-green-600 uppercase">home</span>
                                            )}
                                        </label>
                                    ))}
                                    {filteredCountries.length === 0 && (
                                        <p className="col-span-full text-center text-gray-400 py-6 text-sm">No countries match "{countrySearch}"</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SettingsPage
